import CardEditor from './CardEditor';
import { RootState, useAppSelector } from '@Store/store';
import { ButtonTextData, CardData } from '@Types/common';
import { validateUrl } from '@Utils/common';
import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useGenerateShortURLMutation } from '../apiSlice';



interface ButtonTextProp {
  text: string;
  buttonText?: ButtonTextData[];
}
interface ResponseStepProps {
  data: {
    autofollowData: {
      status: boolean;
      data?: {
        followcheckMessage?: {
          text: string;
          buttonText: ButtonTextData[];
        };
        openingMessage?: {
          text: string;
          buttonText: ButtonTextData[];
        };
      };
    };
    responseData: {
      type: MessageType;
      data: ButtonTextProp | CardData[] | null;
    };
  };
  onUpdate: (data: any) => void;
  automationId: string | undefined;
  triggerData: any;
}

type MessageType = 'text' | 'card' | 'INSTAGRAM_DM' | null;


const ResponseStep: React.FC<ResponseStepProps> = ({ data, onUpdate, automationId, triggerData }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [selectedMessageType, setSelectedMessageType] = useState<MessageType>(data?.responseData?.type || null);
  const [text, setText] = useState('');
  const [buttonText, setButtonText] = useState<ButtonTextData[]>([]);
  const [cardData, setCardData] = useState<CardData[]>([]);
  const [autoFollow, setAutoFollow] = useState(data?.autofollowData?.status || false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalType, setInfoModalType] = useState<'opening' | 'follow' | null>(null);
  const [buttonErrors, setButtonErrors] = useState<{ [key: number]: string }>({});
  const [showMobileMockup, setShowMobileMockup] = useState(false);

  const { user } = useAppSelector((state: RootState) => state.profile);

  const [generateShortURL, { isLoading: generateShortURLIsLoading }] = useGenerateShortURLMutation()



  // Add useEffect to update state when data changes
  useEffect(() => {
    setSelectedMessageType(data?.responseData?.type || null);
    setAutoFollow(data?.autofollowData?.status || false);

    if (data?.responseData?.type === 'card') {
      if (Array.isArray(data?.responseData?.data)) {
        setCardData(data.responseData.data); // ✅ Now safe
      } else {
        setCardData([]); // fallback or handle error case
      }
    } else if (data?.responseData?.type === 'text') {
      const textData = data.responseData.data as ButtonTextProp;
      setText(textData?.text || '');
      setButtonText(textData?.buttonText || []);
    }
  }, [data]);


  const [editingMessage, setEditingMessage] = useState<'opening' | 'follow' | null>(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [editMessageButtons, setEditMessageButtons] = useState<ButtonTextData[]>([]);
  const [editingButtonIndex, setEditingButtonIndex] = useState<number | null>(null);

  const handleInfoClick = (type: 'opening' | 'follow') => {
    setInfoModalType(type);
    setIsInfoModalOpen(true);
  };

  const getInfoContent = (type: 'opening' | 'follow') => {
    if (type === 'opening') {
      return "The opening message is the first message your followers will see when they interact with your automation.";
    }
    return "The follow check message appears when a user hasn't followed your profile yet. It encourages them to follow before receiving the content.";
  };


  const handleButtonTextChange = (newText: string) => {
    setText(newText);
    onUpdate({
      ...data,
      responseData: {
        ...data.responseData,
        data: {
          ...data.responseData.data,
          text: newText
        }
      }
    });
  };



  const handleButtonDataChange = (newButtons: ButtonTextData[]) => {
    const errors: { [key: number]: string } = {};
    newButtons.forEach((btn, idx) => {
      if (btn.type === 'link' && btn.data && !validateUrl(btn.data)) {
        errors[idx] = 'Please enter a valid URL';
      }
    });
    setButtonErrors(errors);
    setButtonText(newButtons);
    onUpdate({
      ...data,
      responseData: {
        ...data.responseData,
        data: {
          ...data.responseData.data,
          buttonText: newButtons
        }
      }
    });
  };


  const handleMessageTypeSelect = (type: MessageType) => {
    setSelectedMessageType(type);

    let newResponseData = null;

    if (type === 'card') {
      const defaultCard = [{
        title: '',
        subtitle: '',
        image_url: '',
        buttons: []
      }];
      setCardData(defaultCard);
      newResponseData = {
        type,
        data: defaultCard
      };
    } else if (type === 'text') {
      newResponseData = {
        type,
        data: {
          text: "",
          buttonText: []
        }
      };
    }

    onUpdate({
      ...data, // ✅ keep the rest of the data including autofollowData
      responseData: newResponseData
    });
  };

  const handleSave = (responseType: string) => {
    const updatedData = { ...data, responseData: { type: responseType }, autoFollow }
    onUpdate(updatedData);
    setIsEditModalOpen(false);
  };


  const [isEditing, setIsEditing] = useState<number | null>(null);

  const handleSaveButton = async (idx: number) => {
    if (!buttonText[idx].title || (buttonText[idx].type === 'link' && !buttonText[idx].data)) {
      return;
    }

    const postData = {
      automation_id: automationId,
      original_url: buttonText[idx].data
    }
    
    try {
      const resp = await generateShortURL(postData).unwrap();
      if (resp) {
        const newButtons = [...buttonText];
        newButtons[idx] = { ...buttonText[idx], short_url: resp?.short_url };
        handleButtonDataChange(newButtons);
      }
    } catch (error: any) {
      console.error(error)
    }

    setIsEditing(null);

    
  };

  const handleAutoFollowToggle = () => {
    const newAutoFollow = !autoFollow;
    setAutoFollow(newAutoFollow);

    if (newAutoFollow) {
      // Set default opening message
      const openingMessage = {
        text: "Hey there!\nI'm so happy you're here, thank you so much for your interest 😊\n\nClick below and I'll send you the link in just a sec ✨",
        buttonText: [{
          title: "Send me the link",
          data: `followerCheck#${user?.user_id}#${automationId}#openingMsg#${triggerData?.selectedMedia?.id}`,
          type: "postback" as const
        }]
      };

      // Set default follow check message
      const followcheckMessage = {
        text: "Oh no! It seems you're not following me 👀 \n\nThis content is for the fam only 🔒\nWanna be part of the inner circle? Just hit that Follow button on my profile 😊\nOnce you're in, tap \"I’m following\" below to unlock the good stuff ✨\nLet’s make it official — welcome to the crew 💛",
        buttonText: [
          {
            title: "Visit Profile",
            data: `https://instagram.com/${user?.ig_ids[0]?.username}`, // This will be replaced with actual profile URL
            type: "link" as const,
            subtype: "follow"
          },
          {
            title: "I'm following ✓",
            data: `followerCheck#${user?.user_id}#${automationId}#followingMsg#${triggerData?.selectedMedia?.id}`,
            type: "postback" as const
          }
        ]
      };

      onUpdate({
        ...data,
        autofollowData: {
          status: true,
          data: {
            followcheckMessage,
            openingMessage
          }
        },
      });
    } else {
      onUpdate({
        ...data,
        autofollowData: {
          status: false,
          data: undefined
        }
      });
    }
  };


  const handleEditMessage = (type: 'opening' | 'follow') => {
    if (type === 'opening') {
      setEditMessageText(data.autofollowData.data?.openingMessage?.text || '');
      setEditMessageButtons(data.autofollowData.data?.openingMessage?.buttonText || []);
    } else {
      setEditMessageText(data.autofollowData.data?.followcheckMessage?.text || '');
      setEditMessageButtons(data.autofollowData.data?.followcheckMessage?.buttonText || []);
    }
    setEditingMessage(type);
    setIsMessageModalOpen(true);
  };

  const handleSaveMessage = () => {
    if (editingMessage === 'opening') {
      const updatedOpeningMessage = {
        text: editMessageText,
        buttonText: editMessageButtons
      };
      onUpdate({
        ...data,
        autofollowData: {
          ...data.autofollowData,
          data: {
            ...data.autofollowData.data,
            openingMessage: updatedOpeningMessage
          }
        }
      });
    } else {
      const updatedFollowMessage = {
        text: editMessageText,
        buttonText: editMessageButtons
      };
      onUpdate({
        ...data,
        autofollowData: {
          ...data.autofollowData,
          data: {
            ...data.autofollowData.data,
            followcheckMessage: updatedFollowMessage
          }
        }
      });
    }
    setIsMessageModalOpen(false);
    setEditingMessage(null);
    setEditingButtonIndex(null);
  };


  const handleCardMessageChange = (cardData: CardData[]) => {

    setCardData(cardData)
    onUpdate({
      ...data,
      responseData: {
        ...data.responseData,
        data: cardData
      }
    })
  }

  const renderMessageContent = () => {


    if (selectedMessageType === 'card') {
      return (<div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#7373ff] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="font-medium">Card Message</h4>
          </div>
          <button
            onClick={() => setSelectedMessageType(null)}
            className="text-[#7373ff] hover:text-[#3535d5] text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit
          </button>
        </div>
        <CardEditor automationId={automationId} cards={cardData} onCardsChange={handleCardMessageChange} showMobileMockupCallback={(ssmm: boolean) => setShowMobileMockup(ssmm)} />
      </div>)
    }

    if (selectedMessageType === 'text') {
      return (<div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#7373ff] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="font-medium">Button Message</h4>
          </div>
          <button
            onClick={() => setSelectedMessageType(null)}
            className="text-[#7373ff] hover:text-[#3535d5] text-sm font-medium flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit
          </button>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className="space-y-4">
            <textarea
              value={text}
              onChange={(e) => handleButtonTextChange(e.target.value)}
              placeholder="Enter text..."
              className="h-[10vh] w-full rounded-xl p-3 border-2 border-dotted"
              style={{
                height: `${40 + text.split("\n").length * 20}px`,
                outline: 'none'
              }}
            />
            <div className="flex flex-col gap-4">
              {buttonText.map((btn, idx) => (
                <div key={idx} className="space-y-2">
                  {isEditing === idx ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <div className="relative w-full">
                          <input
                            value={btn.title}
                            onChange={(e) => {
                              const newValue = e.target.value.slice(0, 20);
                              const newButtons = [...buttonText];
                              newButtons[idx] = { ...btn, title: newValue };
                              handleButtonDataChange(newButtons);
                            }}
                            placeholder="Enter Button Text"
                            className="py-2 px-4 pr-16 rounded-xl border-2 border-dotted h-[45px] w-full focus:outline-none focus:border-purple-400"
                            maxLength={20}
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                            {btn.title.length}/20
                          </span>
                        </div>
                        <select
                          value={btn.type}
                          onChange={(e) => {
                            const newButtons = [...buttonText];
                            newButtons[idx] = { ...btn, type: e.target.value as 'link' | 'postback' };
                            handleButtonDataChange(newButtons);
                          }}
                          className="h-[45px] px-2 rounded-xl border-2 border-dotted focus:outline-none focus:border-purple-400"
                        >
                          <option value="link">Link</option>
                          <option value="message" disabled>Postback</option>
                        </select>
                      </div>
                      {btn.type === 'link' && (
                        <input
                          value={btn.data}
                          onChange={(e) => {
                            const newButtons = [...buttonText];
                            newButtons[idx] = { ...btn, data: e.target.value };
                            handleButtonDataChange(newButtons);
                          }}
                          placeholder="Enter URL"
                          className={`w-full py-2 px-4 rounded-xl border-2 border-dotted focus:outline-none ${buttonErrors[idx] ? 'border-red-400' : 'focus:border-purple-400'}`}
                        />
                      )}
                      {buttonErrors[idx] && (
                        <p className="text-sm text-red-500">{buttonErrors[idx]}</p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            const newButtons = buttonText.filter((_, i) => i !== idx);
                            handleButtonDataChange(newButtons);
                            setIsEditing(null);
                          }}
                          disabled={generateShortURLIsLoading}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSaveButton(idx)}
                          disabled={generateShortURLIsLoading || !btn.title || (btn.type === 'link' && !btn.data)}
                          className="px-4 py-2 bg-[#7373ff] text-white rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {generateShortURLIsLoading && <span className='loading_spinner'></span>}Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 py-2 px-4 rounded-xl border group relative">
                      <div className="flex text-center gap-2 w-full">
                        <span className="text-sm font-medium w-full">{btn.title}</span>
                      </div>
                      <div className="flex items-center gap-3 transition-opacity">
                        <button
                          onClick={() => setIsEditing(idx)}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            const newButtons = buttonText.filter((_, i) => i !== idx);
                            handleButtonDataChange(newButtons);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {buttonText.length < 2 && !isEditing && (
              <button
                onClick={() => {
                  const newButtons = [...buttonText, { title: '', data: '', type: 'link' as const }];
                  handleButtonDataChange(newButtons);
                  setIsEditing(newButtons.length - 1);
                }}
                className="w-full py-2 px-4 border-2 border-dashed rounded-xl hover:border-purple-400 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm text-gray-500">Add Button</span>
              </button>
            )}
          </div>
        </div>
      </div>)
    }
    // Add card content rendering here
    return null;
  };


  const renderContent = () => {
    if (!data.responseData.type) {
      return (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-purple-400 transition-colors cursor-pointer"
          onClick={() => setIsEditModalOpen(true)}>
          <div className="flex items-center justify-center">
            <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>
          <p className="text-center mt-2 text-gray-600">Click to edit response</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg p-4 relative border border-gray-200">
        <button
          onClick={() => setIsEditModalOpen(true)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#7373ff] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <h3 className="text-base font-medium">Send Instagram Message</h3>
          </div>


          {(triggerData?.triggerType == "POSTREELCOMMENT" || triggerData?.triggerType == "LIVECOMMENT") && <div className="flex items-center justify-between py-3">
            <span className="text-sm md:text-md font-medium">Automatically ask for a follow</span>
            <button
              onClick={handleAutoFollowToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoFollow ? 'bg-[#7373ff]' : 'bg-gray-200'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoFollow ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>}

          {autoFollow && (
            <div className="space-y-4 mt-1">
              <div className="space-y-3 bg-white rounded-[20px] md:p-4 p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium">Opening Message</h4>
                    <button
                      onClick={() => handleInfoClick('opening')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                  <button
                    onClick={() => handleEditMessage('opening')}
                    className="text-[#7373ff] hover:text-[#3535d5] text-sm font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit
                  </button>
                </div>
                <div className='max-w-[400px] mx-auto rounded-[20px] space-y-6 border p-6'>
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {data.autofollowData?.data?.openingMessage?.text || ''}
                  </div>
                  {data.autofollowData?.data?.openingMessage?.buttonText?.map((btn, idx) => (
                    <div key={idx} className="py-2 px-4 border rounded-xl text-center text-sm font-medium text-[#3535d5]">
                      {btn.title}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3 bg-white rounded-[20px] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium">Follow Check Message</h4>
                    <button
                      onClick={() => handleInfoClick('follow')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                  <button
                    onClick={() => handleEditMessage('follow')}
                    className="text-[#7373ff] hover:text-[#3535d5] text-sm font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit
                  </button>
                </div>
                <div className='max-w-[400px] mx-auto rounded-[20px] space-y-6 border p-6'>
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {data?.autofollowData?.data?.followcheckMessage?.text}
                  </div>
                  <div className='space-y-4'>
                    {data?.autofollowData?.data?.followcheckMessage?.buttonText.map((btn: ButtonTextData, idx: number) => (
                      <div>
                        <div key={idx} className="py-2 px-4 border  rounded-xl text-center text-sm font-medium text-[#3535d5]">
                          {btn.title}
                        </div>
                        {btn.type == "link" && btn.subtype == "follow" && (
                          <p className="text-xs text-center text-gray-500">(On clicking this user will be taken to your IG account)</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}


          <div className="space-y-2">
            <h4 className="text-sm md:text-md font-medium">Add content for your message</h4>
            {(selectedMessageType == "INSTAGRAM_DM" || !selectedMessageType) && (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleMessageTypeSelect('text')}
                  className="p-4 border rounded-lg flex flex-col items-center gap-3 transition-colors hover:border-gray-300"
                >
                  <div className="w-12 h-12 bg-[#7373ff] rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h4 className="font-medium">Text</h4>
                    <p className="text-sm text-gray-500">Texts & Text w Button</p>
                  </div>
                </button>

                <button
                  onClick={() => handleMessageTypeSelect('card')}
                  className="p-4 border rounded-lg flex flex-col items-center gap-3 transition-colors hover:border-gray-300"
                >
                  <div className="w-12 h-12 bg-[#7373ff] rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h4 className="font-medium">Cards</h4>
                    <p className="text-sm text-gray-500">Image, Text, Buttons</p>
                    {/* <span className="px-3 text-xs text-[#7373ff] border border-[#7373ff] rounded-full">Coming Soon</span> */}

                  </div>
                </button>
              </div>
            )}
            {
              renderMessageContent()
            }
          </div>
        </div>
      </div>
    );
  };




  return (
    <div className={`${selectedMessageType == "card" && showMobileMockup ? 'max-w-[900px] w-full' : 'max-w-[600px] w-full'} mx-auto bg-white rounded-[20px] p-4  md:p-6`}>
      <h2 className="text-lg font-semibold mb-4">Step 2: Response To Trigger</h2>
      {renderContent()}
      <ReactModal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        className="m-2 relative max-w-[500px] w-full p-6 rounded-xl border-none bg-white"
        overlayClassName="z-20 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        ariaHideApp={false}
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Next Step</h3>
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500">Set the next block in the workflow</p>

          <div className="space-y-2">
            <button
              onClick={() => handleSave('INSTAGRAM_DM')}
              className="w-full px-3 py-1  flex items-center gap-3 border rounded-lg hover:border-[#0084ff] transition-colors"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <span className="font-regular text-sm">Send Instagram Message</span>
            </button>

            <button
              disabled
              className="w-full px-3 py-1 flex items-center justify-between border rounded-lg text-gray-400 cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-xs">Collect User Emails</span>
              </div>
              <span className="text-xs text-purple-500 bg-slate-100 px-2 py-1 rounded">Coming soon</span>
            </button>
          </div>
        </div>
      </ReactModal>
      <ReactModal
        isOpen={isMessageModalOpen}
        onRequestClose={() => {
          setIsMessageModalOpen(false);
          setEditingMessage(null);
          setEditingButtonIndex(null);
        }}
        className="relative top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[500px] p-6 bg-white rounded-xl shadow-lg"
        overlayClassName="z-10 fixed inset-0 bg-black bg-opacity-50"
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">
              Edit {editingMessage === 'opening' ? 'Opening' : 'Follow Check'} Message
            </h3>
            <button
              onClick={() => {
                setIsMessageModalOpen(false);
                setEditingMessage(null);
                setEditingButtonIndex(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <textarea
            value={editMessageText}
            onChange={(e) => setEditMessageText(e.target.value)}
            className="w-full h-32 p-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Enter message text..."
          />

          <div className="space-y-3">
            <h4 className="text-sm font-medium">Buttons</h4>
            {editMessageButtons.map((btn, idx) => (
              <div key={idx} className="space-y-2">
                {editingButtonIndex === idx ? (
                  <div className="space-y-2">
                    <input
                      value={btn.title}
                      onChange={(e) => {
                        const newValue = e.target.value.slice(0, 20);
                        const newButtons = [...editMessageButtons];
                        newButtons[idx] = { ...btn, title: newValue };
                        setEditMessageButtons(newButtons);
                      }}
                      className="w-full text-center px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                      maxLength={20}
                      placeholder="Enter button text"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingButtonIndex(null)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => setEditingButtonIndex(null)}
                        className="px-3 py-1 text-sm text-white bg-[#7373ff] rounded-lg hover:bg-purple-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-2 border rounded-xl">
                    <p className="text-sm w-full text-center">{btn.title}</p>
                    <button
                      onClick={() => setEditingButtonIndex(idx)}
                      className=" text-[#7373ff] hover:text-[#3535d5]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
                {btn.type == "link" && btn.subtype == "follow" && (
                  <p className="text-xs text-center text-gray-500">(On clicking this user will be taken to your IG account)</p>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              onClick={() => {
                setIsMessageModalOpen(false);
                setEditingMessage(null);
                setEditingButtonIndex(null);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveMessage}
              className="px-4 py-2 text-white bg-[#7373ff] rounded-lg hover:bg-purple-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </ReactModal>
      <ReactModal
        isOpen={isInfoModalOpen}
        onRequestClose={() => setIsInfoModalOpen(false)}
        className="m-2 relative max-w-[500px] w-full p-6 rounded-xl border-none bg-white"
        overlayClassName="z-10 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        ariaHideApp={false}
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">
              {infoModalType === 'opening' ? 'Opening Message' : 'Follow Check Message'} Info
            </h3>
            <button
              onClick={() => setIsInfoModalOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {infoModalType && getInfoContent(infoModalType)}
          </p>
        </div>
      </ReactModal>
    </div>
  );
};

export default ResponseStep;

